import PropTypes from 'prop-types';
import React from 'react';
import { scrollToElement } from 'utils/scroll';

let components;

/**
 * @overview Makes a component scrollableTo
 * @param {class} WrappedComponent The component to allow to be scrolled to.
 * @param {boolean} [requiresIdProp = true] Determines if the component is scrollable to, or only scrolls to another component (i.e. CTA vs Value Prop)
 * @returns {class} WrappedComponent with methods that allow for scrolling to a component based on the `id` prop supplied to it.
 */
export default function scrollableTo(WrappedComponent, requiresIdProp = true) {
  class ScrollableTo extends React.Component {
    constructor(props, context) {
      super(props, context);
      this.getScrollableWaypointRef = this.getScrollableWaypointRef.bind(this);
      this.handleScrollToId = this.handleScrollToId.bind(this);
    }

    getScrollableWaypointRef(id) {
      const foundComponent = components.find((c) => c.props.id === id);

      return foundComponent ? foundComponent.waypoint : null;
    }

    componentDidMount() {
      if (components) {
        components.push(this);
      } else {
        components = [this];
      }
    }

    componentWillUnmount() {
      components = undefined;
    }

    handleScrollToId(id) {
      const el = this.getScrollableWaypointRef(id);
      if (el) {
        scrollToElement(el);
      }
    }

    renderWrappedComponent() {
      return (
        <WrappedComponent
          getScrollableWaypointRef={this.getScrollableWaypointRef}
          handleScrollToId={this.handleScrollToId}
          {...this.props}
        />
      );
    }

    render() {
      if (requiresIdProp) {
        return (
          <span ref={(waypoint) => { this.waypoint = waypoint; }}>
            {this.renderWrappedComponent()}
          </span>
        );
      }

      return this.renderWrappedComponent();
    }
  }

  if (requiresIdProp) {
    ScrollableTo.propTypes = {
      id: PropTypes.string.isRequired,
    };
  }

  return ScrollableTo;
}
